import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { Overlay } from "src/types/osbuilder";
import { useFetchProductByIdQuery, useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { makeStyles } from "tss-react/mui";

import { closeDetails } from "@dashboard/devices/store";
import { AndroidModule, ApplicationModule, DownloadableFileModule, ExecutableModule, Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, Chip, Theme, Typography } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";

import { ClosePanelButton } from "../shared/ClosePanelButton";
import { PaneElement } from "../shared/PaneElement";

import { AddOverlayDialog } from "./AddOverlayDialog/AddOverlayDialog";
import { ApplicationDialog } from "./ApplicationDialog/ApplicationDialog";
import { BootAnimationDialog } from "./BootAnimationDialog/BootAnimationDialog";
import { DownloadableFileDialog } from "./DownloadableFileDialog/DownloadableFileDialog";
import { ExecutableDialog } from "./ExecutableDialog/ExecutableDialog";
import {
    CustomOverlayCard,
    DownloadableFileCard,
    ExistingModuleCard,
    PrebuiltApplicationCard,
    PrebuiltBootAnimationCard,
    PrebuiltExecutableCard,
    RemoveOverrideCard,
} from "./ModuleCard/ModuleCard";
import { ExcludeModuleDialog } from "./ExcludeModuleDialog";
import { IncludeModuleDialog } from "./IncludeModuleDialog";
import Utils from "./ModulePanel.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    contentBox: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        flex: 1,
        // Do not give this a percentage min-height: the panel root is a fixed 100vh column, so a
        // percentage minimum sizes this scrollport to the whole screen even though it starts below
        // the header, pushing its last rows under the bottom edge with nothing able to scroll them
        // into view.
        minHeight: 0,
        overflowY: "auto",
        padding: theme.spacing(3),
    },
    header: { display: "flex", flexDirection: "column" },
    searchContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(8),
    },
    tagsContainer: { display: "flex", flexDirection: "row", gap: theme.spacing(1) },
    gridContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: theme.spacing(4),
    },
    searchContainerBox: { paddingLeft: theme.spacing(3), paddingRight: theme.spacing(3) },
    separator: {
        height: "1px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(6),
    },
}));
type Props = {
    productId: number;
};
export const ModulePanel = (props: Props) => {
    const [openAddOwnApplicationDialog, setOpenAddOwnApplicationDialog] = React.useState(false);
    const [editedApplication, setEditedApplication] = React.useState<ApplicationModule | null>(null);
    const [openAddOwnExecutableDialog, setOpenAddOwnExecutableDialog] = React.useState(false);
    const [editedExecutable, setEditedExecutable] = React.useState<ExecutableModule | null>(null);
    const [openBootAnimationDialog, setOpenBootAnimationDialog] = React.useState(false);
    const [openIncludeSystemPackageDialog, setOpenIncludeSystemPackageDialog] = React.useState(false);
    const [openExcludeSystemPackageDialog, setOpenExcludeSystemPackageDialog] = React.useState(false);
    const [openAddDownloadableFileDialog, setOpenAddDownloadableFileDialog] = React.useState(false);
    const [editedDownloadableFile, setEditedDownloadableFile] = React.useState<DownloadableFileModule | null>(null);
    const [openAddOverlayDialog, setOpenAddOverlayDialog] = React.useState(false);

    const productResult = useFetchProductByIdQuery(props.productId);
    const { data: product } = productResult;
    useQueryErrorToast(productResult);

    const [selectedTag, setSelectedTag] = React.useState<string>(
        product?.customBuildConfig?.modules.length || product?.customBuildConfig?.overlays?.length ? "Added" : "All",
    );

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [updateProduct, { isLoading: isUpdatingProduct }] = useUpdateProductMutation();
    const { t } = useTranslation(["sidepanels", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const addedModules: AndroidModule[] = product?.customBuildConfig?.modules || [];
    const matchingAddedModules = Utils.filterModules(addedModules, selectedTag);
    const matchingModuleTemplates = Utils.filterModules(Utils.addableModuleTemplates, selectedTag);
    const availableModuleTemplates = Utils.excludeAlreadyAddedModules(matchingModuleTemplates, matchingAddedModules);

    const overlays: Overlay[] = product?.customBuildConfig?.overlays ?? [];
    const isOverlayTemplateVisible = selectedTag === "All" || selectedTag === "Build";

    const allShownCards = [...matchingAddedModules, ...availableModuleTemplates];
    const allTags = Utils.getTags([...addedModules, ...Utils.addableModuleTemplates]);

    // No template card carries the "Added" tag, so once the last module and overlay are gone that
    // chip would leave the panel empty with nothing to add. Send the user back to "All" instead.
    const resetTagWhenNothingAdded = (config: Product["customBuildConfig"]) => {
        if (!config?.modules.length && !config?.overlays?.length) {
            setSelectedTag("All");
        }
    };

    const onDirectAddModule = (module: AndroidModule) => {
        if (!product) {
            return;
        }
        const newProduct = _.cloneDeep(product);
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig.modules = [...addedModules, module];
        }
        void Utils.saveProduct(updateProduct, newProduct);
    };

    const onDirectRemoveModule = (module: AndroidModule) => {
        const moduleIndex = product ? Utils.findModuleIndex(addedModules, module) : -1;
        if (!product || moduleIndex === -1) {
            return;
        }
        const remainingModules = addedModules.filter((_item, itemIndex) => itemIndex !== moduleIndex);
        const newProduct = { ...product };
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig = {
                ...newProduct.customBuildConfig,
                modules: remainingModules,
                sources: Utils.dropUnreferencedSource(
                    newProduct.customBuildConfig.sources,
                    remainingModules,
                    typeof module.sourceId === "string" ? module.sourceId : undefined,
                ),
            };
        }
        resetTagWhenNothingAdded(newProduct.customBuildConfig);
        void Utils.saveProduct(updateProduct, newProduct);
    };

    const onRemoveOverlay = (index: number) => {
        if (!product) {
            return;
        }
        const newProduct = _.cloneDeep(product);
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig.overlays = overlays.filter((_item, itemIndex) => itemIndex !== index);
        }
        resetTagWhenNothingAdded(newProduct.customBuildConfig);
        void Utils.saveProduct(updateProduct, newProduct);
    };

    const getTooltipTitle = () => {
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!product) {
            return t("common:tooltip.noProductSelected");
        }
        if (isUpdatingProduct) {
            return t("common:tooltip.productUpdateInProgress");
        }
        return undefined;
    };

    const createCards = (modules: AndroidModule[]) => {
        return modules.map((module) => {
            // A template carries an empty sourceId and an added module a unique one, so the type keeps an
            // include and an exclude of the same package apart. Index keys would hand a removed card's
            // pending state to the one that slides into its place.
            const cardKey = module.type + (module.sourceId || module.name);

            if (module.type === "prebuilt_app") {
                return (
                    <PrebuiltApplicationCard
                        key={cardKey}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => {
                            setEditedApplication(null);
                            setOpenAddOwnApplicationDialog(true);
                        }}
                        onEdit={() => {
                            setEditedApplication(module);
                            setOpenAddOwnApplicationDialog(true);
                        }}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "prebuilt_executable") {
                return (
                    <PrebuiltExecutableCard
                        key={cardKey}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => {
                            setEditedExecutable(null);
                            setOpenAddOwnExecutableDialog(true);
                        }}
                        onEdit={() => {
                            setEditedExecutable(module);
                            setOpenAddOwnExecutableDialog(true);
                        }}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "remove_override") {
                return (
                    <RemoveOverrideCard
                        key={cardKey}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenExcludeSystemPackageDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "prebuilt_bootanimation") {
                return (
                    <PrebuiltBootAnimationCard
                        key={cardKey}
                        module={module}
                        sources={product?.customBuildConfig?.sources ?? {}}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenBootAnimationDialog(true)}
                        onEdit={() => setOpenBootAnimationDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "downloadable_file") {
                return (
                    <DownloadableFileCard
                        key={cardKey}
                        module={module}
                        sources={product?.customBuildConfig?.sources ?? {}}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => {
                            setEditedDownloadableFile(null);
                            setOpenAddDownloadableFileDialog(true);
                        }}
                        onEdit={() => {
                            setEditedDownloadableFile(module);
                            setOpenAddDownloadableFileDialog(true);
                        }}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else {
                return (
                    <ExistingModuleCard
                        key={cardKey}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onDirectAddModule={() => onDirectAddModule(module)}
                        onIncludeSystemPackage={() => setOpenIncludeSystemPackageDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            }
        });
    };

    if (!product) {
        return null;
    }

    return (
        <Utils.SearchProvider>
            {({ onChange }) => (
                <React.Fragment>
                    <PaneElement.HeaderBox>
                        <Box className={classes.header}>
                            <Typography variant="h6">{t("modulePanel.title")}</Typography>
                            {product?.customBuildConfig?.productName ? (
                                <Typography variant="body1">{Utils.getProductHeaderTitle(product)}</Typography>
                            ) : null}
                        </Box>
                        <ClosePanelButton onClick={() => dispatch(closeDetails())} />
                    </PaneElement.HeaderBox>
                    <Box className={classes.searchContainerBox}>
                        <PaneElement.Header>{t("sidepanels:modulePanel.header")}</PaneElement.Header>
                        <Box className={classes.searchContainer}>
                            <QueryBuilderField
                                placeholder={t("common:button.search")}
                                onChange={onChange}
                                onClear={() => onChange("")}
                            />

                            <Box className={classes.tagsContainer}>
                                {allTags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        color="primary"
                                        size="small"
                                        variant={selectedTag === tag ? "filled" : "outlined"}
                                        onClick={() => setSelectedTag(tag)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.contentBox}>
                        <Box className={classes.gridContainer}>
                            {createCards(addedModules)}
                            {overlays.map((overlay, index) => (
                                <CustomOverlayCard
                                    key={`${overlay.targetPackage}|${overlay.resourcePath}|${overlay.resourceType}|${overlay.resourceName}`}
                                    overlay={overlay}
                                    onRemove={() => onRemoveOverlay(index)}
                                    isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                                    tooltip={getTooltipTitle()}
                                />
                            ))}
                        </Box>
                        {(addedModules.length || overlays.length) &&
                        (availableModuleTemplates.length || isOverlayTemplateVisible) ? (
                            <Box className={classes.separator} />
                        ) : null}
                        <Box className={classes.gridContainer}>
                            {createCards(availableModuleTemplates)}
                            {isOverlayTemplateVisible ? (
                                <CustomOverlayCard
                                    onAdd={() => setOpenAddOverlayDialog(true)}
                                    isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                                    tooltip={getTooltipTitle()}
                                />
                            ) : null}
                        </Box>
                        {!allShownCards.length && !overlays.length && !isOverlayTemplateVisible ? (
                            <PaneElement.NoData message={t("modulePanel.noModulesFound")} />
                        ) : null}

                        <ApplicationDialog
                            product={product}
                            module={editedApplication}
                            open={openAddOwnApplicationDialog}
                            onToggle={setOpenAddOwnApplicationDialog}
                        />
                        <BootAnimationDialog
                            product={product}
                            open={openBootAnimationDialog}
                            onToggle={setOpenBootAnimationDialog}
                        />
                        <DownloadableFileDialog
                            product={product}
                            module={editedDownloadableFile}
                            open={openAddDownloadableFileDialog}
                            onToggle={setOpenAddDownloadableFileDialog}
                        />
                        <ExecutableDialog
                            product={product}
                            module={editedExecutable}
                            open={openAddOwnExecutableDialog}
                            onToggle={setOpenAddOwnExecutableDialog}
                        />
                        <AddOverlayDialog product={product} open={openAddOverlayDialog} onToggle={setOpenAddOverlayDialog} />
                        <ExcludeModuleDialog
                            product={product}
                            open={openExcludeSystemPackageDialog}
                            onToggle={setOpenExcludeSystemPackageDialog}
                        />
                        <IncludeModuleDialog
                            product={product}
                            open={openIncludeSystemPackageDialog}
                            onToggle={setOpenIncludeSystemPackageDialog}
                        />
                    </Box>
                </React.Fragment>
            )}
        </Utils.SearchProvider>
    );
};
